import React from 'react';
import { motion } from 'motion/react';
import { Check, Gift, Coins, Trophy, Ticket, Star, Zap, Package, Crown, Lock } from 'lucide-react';
import { MissionDefinition, MissionType, MissionReward } from '../utils/missions';
import { UserMission } from '../hooks/useMissions';
import { cn } from '../lib/utils';
import CurrencyIcon from './CurrencyIcon';
import ChestIcon from './ChestIcon';
import { audioEngine } from '../utils/audioEngine';


interface MissionCardProps {
  missionDef: MissionDefinition;
  userMission: UserMission;
  onClaim: (id: string) => Promise<boolean | undefined>;
  isPremium: boolean;
  onLockClick: () => void;
}

export default function MissionCard({ missionDef, userMission, onClaim, isPremium, onLockClick }: MissionCardProps) {
  const progressPercent = Math.min(100, (userMission.progress / missionDef.goal) * 100);
  const isCompleted = userMission.progress >= missionDef.goal;
  const isClaimed = userMission.isClaimed;
  const isLockedPro = missionDef.isPremiumOnly && !isPremium;

  const renderReward = (reward: MissionReward, idx: number) => {
    const icon = () => {
      switch (reward.type) {
        case 'coins': return <CurrencyIcon type="coin" size="sm" />;
        case 'tickets': return <CurrencyIcon type="ticket_heart" size="sm" />;
        case 'exp': return <CurrencyIcon type="exp" size="sm" />;
        case 'badge': return <Star className="w-3 h-3 text-cyan-400 fill-current" />;
        case 'gems': return <CurrencyIcon type="gem" size="sm" />;
        case 'prism': return <CurrencyIcon type="prism" size="sm" stars={reward.stars} />;
        case 'box': return <div className="scale-75 -mx-1.5"><ChestIcon tier={reward.subType as any || 'bronze'} size="xs" /></div>;
        case 'star_ticket': return <CurrencyIcon type="ticket_star" size="sm" />;
        default: return <Gift className="w-3 h-3 text-cyan-500" />;
      }
    };

    let displayAmount = "";
    const amount = typeof reward.amount === 'number' ? reward.amount : parseInt(reward.amount as string) || 0;
    
    if (reward.type === 'prism') {
      const starCount = reward.stars || 1;
      displayAmount = amount > 1 ? `${starCount}★ Tkt x${amount}` : `${starCount}★ Tkt`;
    } else {
      displayAmount = amount > 1 ? amount.toLocaleString() : "";
    }

    return (
      <div key={idx} className="bg-slate-50 px-2 py-1 rounded-xl border border-slate-100 flex items-center gap-1.5 shadow-sm">
        {icon()}
        {displayAmount && (
          <span className="font-num text-[10px] font-black text-slate-700">
              {displayAmount}
          </span>
        )}
      </div>
    );
  };

  return (
    <div 
      className={cn(
        "bg-white p-5 rounded-[32px] border-2 border-slate-50 shadow-sm flex flex-col gap-4 relative overflow-hidden transition-all",
        isClaimed && "opacity-60 grayscale-[0.5]",
        isLockedPro && "bg-slate-50/50 border-purple-100 shadow-none"
      )}
    >
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {missionDef.type !== 'lifetime' && (
              <span className={cn(
                "text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border",
                missionDef.type === 'daily' ? "text-cyan-600 border-cyan-100 bg-cyan-50" : 
                missionDef.type === 'weekly' ? "text-emerald-600 border-emerald-100 bg-emerald-50" : 
                "text-purple-600 border-purple-100 bg-purple-50"
              )}>
                {missionDef.type}
              </span>
            )}
            {missionDef.isPremiumOnly && (
              <span className="text-[8.5px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border text-amber-600 border-amber-100 bg-amber-50 flex items-center gap-1 shadow-sm">
                <Crown className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                PRO
              </span>
            )}
            {isCompleted && !isClaimed && (
                <motion.span 
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className={cn(
                    "text-[8px] font-black uppercase px-2 py-0.5 rounded-lg shadow-sm",
                    isLockedPro ? "bg-purple-500 text-white" : "bg-emerald-500 text-white"
                  )}
                >
                    {isLockedPro ? "Locked" : "Ready!"}
                </motion.span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <h4 className="text-lg font-num font-bold uppercase tracking-tight text-slate-900 leading-tight">{missionDef.name}</h4>
            {missionDef.type === 'lifetime' && (
              <div className={cn(
                "w-3 h-3 rounded-[3px] shadow-lg animate-pulse shrink-0",
                missionDef.id.split('.')[1] === '1' ? "bg-amber-600 shadow-amber-600/40" :
                missionDef.id.split('.')[1] === '2' ? "bg-slate-400 shadow-slate-400/40" :
                missionDef.id.split('.')[1] === '3' ? "bg-amber-400 shadow-amber-400/40" :
                "bg-cyan-400 shadow-cyan-400/40"
              )} />
            )}
          </div>
          <p className="text-[10px] text-slate-700 font-black uppercase tracking-widest mt-1 leading-relaxed">
            {missionDef.objective}
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-1.5 max-w-[120px] shrink-0">
          {missionDef.rewards.map((r, i) => renderReward(r, i))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center text-[9px] font-black text-slate-700 uppercase tracking-widest">
            <span>Progress</span>
            <span className="font-num text-slate-850">{userMission.progress.toLocaleString()} / {missionDef.goal.toLocaleString()}</span>
        </div>
        
        {/* The "Juicy" Progress Bar */}
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden relative">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            className={cn(
              "h-full rounded-full shadow-[0_2px_0_rgba(255,255,255,0.4)_inset]",
              isLockedPro ? "bg-purple-400" : "bg-emerald-400"
            )}
            transition={{ type: 'spring', bounce: 0.4, duration: 0.8 }}
          />
        </div>
      </div>

      {isCompleted && !isClaimed && (
        isLockedPro ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              audioEngine.playSound('click');
              onLockClick();
            }}
            className="w-full bg-gradient-to-r from-amber-500 to-purple-600 text-white font-num text-sm py-3 rounded-2xl shadow-lg active:shadow-sm mt-2 toy-3d-button border-none flex items-center justify-center gap-2 uppercase tracking-widest font-black"
          >
            <Crown className="w-4 h-4 fill-white text-white" />
            Unlock with Pro
          </motion.button>
        ) : (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              audioEngine.playSound('click');
              onClaim(userMission.id);
            }}
            className="w-full bg-emerald-500 text-white font-num text-sm py-3 rounded-2xl shadow-lg active:shadow-sm mt-2 toy-3d-button border-none"
          >
            CLAIM REWARD
          </motion.button>
        )
      )}

      {isClaimed && (
        <div className="w-full bg-slate-50 text-slate-400 font-num text-center text-sm py-3 rounded-2xl border-2 border-dashed border-slate-100 mt-2 flex items-center justify-center gap-2">
          <Check className="w-4 h-4" />
          CLAIMED
        </div>
      )}
    </div>
  );
}
